/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bug, Lightbulb, MessageSquare, ChevronRight, CheckCircle, Loader, Paperclip } from 'lucide-react';

const MAX_IMAGES = 3;
const MAX_IMAGE_SIZE_MB = 5;

const CATEGORIES = [
  {
    id: 'bug',
    label: 'Τεχνικό Πρόβλημα',
    icon: Bug,
    color: '#EF4444',
    bg: '#FEF2F2',
    border: '#FECACA',
    emoji: '🐛',
    placeholder: 'Περιγράψτε το πρόβλημα που αντιμετωπίσατε. Τι κάνατε όταν εμφανίστηκε;',
  },
  {
    id: 'feedback',
    label: 'Σχόλιο / Παρατήρηση',
    icon: MessageSquare,
    color: '#3B82F6',
    bg: '#EFF6FF',
    border: '#BFDBFE',
    emoji: '💬',
    placeholder: 'Μοιραστείτε τη γνώμη σας για την εφαρμογή. Τι σας άρεσε ή δεν σας άρεσε;',
  },
  {
    id: 'suggestion',
    label: 'Βελτίωση / Πρόταση',
    icon: Lightbulb,
    color: '#F59E0B',
    bg: '#FFFBEB',
    border: '#FDE68A',
    emoji: '💡',
    placeholder: 'Έχετε ιδέα για βελτίωση ή νέα λειτουργία; Περιγράψτε την αναλυτικά.',
  },
];

const FeedbackWidget = ({ currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('select'); // 'select' | 'write' | 'sending' | 'done'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const modalRef = useRef(null);
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (step === 'write' && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [step]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setStep('select');
      setSelectedCategory(null);
      setMessage('');
      setError('');
      setAttachments([]);
    }, 300);
  };

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setStep('write');
    setMessage('');
    setError('');
    setAttachments([]);
  };

  const handleBack = () => {
    setStep('select');
    setSelectedCategory(null);
    setMessage('');
    setError('');
    setAttachments([]);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';

    const remaining = MAX_IMAGES - attachments.length;
    if (remaining <= 0) {
      setError(`Μέχρι ${MAX_IMAGES} εικόνες ανά αναφορά.`);
      return;
    }

    const toProcess = files.slice(0, remaining);

    toProcess.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        setError('Επιτρέπονται μόνο αρχεία εικόνας.');
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        setError(`Η εικόνα "${file.name}" ξεπερνά τα ${MAX_IMAGE_SIZE_MB}MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((prev) => {
          if (prev.length >= MAX_IMAGES) return prev;
          return [...prev, { name: file.name, dataUrl: reader.result }];
        });
      };
      reader.readAsDataURL(file);
    });

    setError('');
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!message.trim() || message.trim().length < 10) {
      setError('Παρακαλώ γράψτε τουλάχιστον 10 χαρακτήρες.');
      return;
    }
    setError('');
    setStep('sending');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          category: selectedCategory.id,
          categoryLabel: selectedCategory.label,
          message: message.trim(),
          userName: currentUser?.name || 'Άγνωστος',
          userEmail: currentUser?.email || '',
          userRole: currentUser?.role || 'employee',
          department: currentUser?.department || '',
          attachments: attachments.map(a => ({ name: a.name, dataUrl: a.dataUrl })),
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStep('done');
        setTimeout(() => handleClose(), 3000);
      } else {
        setError('Αποτυχία αποστολής. Δοκιμάστε ξανά.');
        setStep('write');
      }
    } catch {
      setError('Σφάλμα σύνδεσης. Δοκιμάστε ξανά.');
      setStep('write');
    }
  };

  const cat = selectedCategory;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        title="Αναφορά προβλήματος ή σχόλιο"
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border shadow-lg rounded-full px-4 py-3 hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}
      >
        <div className="relative">
          <MessageSquare className="h-5 w-5 text-gray-600 dark:text-jewel-text group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white dark:border-jewel-bg" />
        </div>
        <span className="text-sm font-semibold text-gray-700 dark:text-jewel-text group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors whitespace-nowrap">
          Feedback
        </span>
      </button>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-4 sm:p-6 pointer-events-none">
          <div
            ref={modalRef}
            className="pointer-events-auto w-full max-w-sm bg-white dark:bg-jewel-bg rounded-2xl shadow-2xl border border-gray-100 dark:border-jewel-border overflow-hidden"
            style={{
              animation: 'slideUp 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
            }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-jewel-border">
              <div className="flex items-center gap-2">
                {step === 'write' && (
                  <button
                    onClick={handleBack}
                    className="p-1 -ml-1 rounded-lg hover:bg-gray-100 dark:hover:bg-jewel-surface transition-colors mr-1"
                  >
                    <ChevronRight className="h-4 w-4 text-gray-400 dark:text-jewel-text-muted rotate-180" />
                  </button>
                )}
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-jewel-text text-sm leading-none">
                    {step === 'select' && 'Πώς μπορούμε να βοηθήσουμε;'}
                    {step === 'write' && cat?.label}
                    {step === 'sending' && 'Αποστολή...'}
                    {step === 'done' && 'Ευχαριστούμε!'}
                  </h3>
                  {step === 'select' && (
                    <p className="text-[11px] text-gray-400 dark:text-jewel-text-muted mt-0.5">Επιλέξτε κατηγορία</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-jewel-surface transition-colors"
              >
                <X className="h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
              </button>
            </div>
            <div className="p-5">
              {/* STEP: SELECT */}
              {step === 'select' && (
                <div className="space-y-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    return (
                      <button
                        key={c.id}
                        onClick={() => handleCategorySelect(c)}
                        className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-150 hover:scale-[1.01] active:scale-[0.99] group"
                        style={{
                          borderColor: c.border,
                          backgroundColor: c.bg,
                        }}
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-base"
                          style={{ backgroundColor: c.color }}
                        >
                          {c.emoji}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-800">{c.label}</p>
                        </div>
                        <ChevronRight
                          className="h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5"
                          style={{ color: c.color }}
                        />
                      </button>
                    );
                  })}
                  <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted text-center pt-2">
                    Συνδεδεμένος ως <span className="font-semibold">{currentUser?.name}</span>
                  </p>
                </div>
              )}

              {/* STEP: WRITE */}
              {step === 'write' && cat && (
                <div>
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm font-semibold"
                    style={{ backgroundColor: cat.bg, color: cat.color }}
                  >
                    <span>{cat.emoji}</span>
                    <span>{cat.label}</span>
                  </div>
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => { setMessage(e.target.value); setError(''); }}
                    placeholder={cat.placeholder}
                    rows={5}
                    className="w-full text-sm border border-gray-200 dark:border-jewel-border bg-white dark:bg-jewel-bg text-gray-900 dark:text-jewel-text rounded-xl px-4 py-3 resize-none focus:outline-none focus:ring-2 transition-all placeholder-gray-300 dark:placeholder-gray-500"
                    style={{ focusRingColor: cat.color }}
                    onFocus={(e) => { e.target.style.borderColor = cat.color; e.target.style.boxShadow = `0 0 0 3px ${cat.color}22`; }}
                    onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = 'none'; }}
                  />
                  <div className="flex items-center justify-between mt-1.5 mb-4">
                    {error ? (
                      <p className="text-[11px] text-red-500 dark:text-red-400 font-medium">{error}</p>
                    ) : (
                      <p className="text-[11px] text-gray-400 dark:text-jewel-text-muted">{message.length} χαρακτήρες</p>
                    )}
                    <p className="text-[11px] text-gray-400 dark:text-jewel-text-muted">ελάχ. 10</p>
                  </div>

                  <div className="mb-4">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    {attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {attachments.map((att, i) => (
                          <div key={i} className="relative group">
                            <img
                              src={att.dataUrl}
                              alt={att.name}
                              className="w-14 h-14 object-cover rounded-lg border border-gray-200 dark:border-jewel-border"
                            />
                            <button
                              onClick={() => removeAttachment(i)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 dark:bg-jewel-text text-white dark:text-jewel-bg flex items-center justify-center shadow-sm hover:bg-gray-900 dark:hover:bg-white transition-colors"
                              title="Αφαίρεση"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {attachments.length < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-jewel-text-muted hover:text-gray-700 dark:hover:text-jewel-text border border-dashed border-gray-300 dark:border-jewel-border rounded-lg px-3 py-1.5 transition-colors"
                      >
                        <Paperclip className="h-3 w-3" />
                        Προσθήκη εικόνας ({attachments.length}/{MAX_IMAGES})
                      </button>
                    )}
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={message.trim().length < 10}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: cat.color }}
                  >
                    <Send className="h-4 w-4" />
                    Αποστολή
                  </button>
                </div>
              )}

              {/* STEP: SENDING */}
              {step === 'sending' && (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <Loader className="h-8 w-8 text-blue-500 animate-spin" />
                  <p className="text-sm text-gray-500 dark:text-jewel-text-muted font-medium">Αποστολή μηνύματος...</p>
                </div>
              )}

              {/* STEP: DONE */}
              {step === 'done' && (
                <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
                  <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 dark:text-jewel-text text-base">Λήφθηκε!</p>
                    <p className="text-sm text-gray-500 dark:text-jewel-text-muted mt-1">
                      Το μήνυμά σας στάλθηκε επιτυχώς.<br />Θα το εξετάσουμε άμεσα.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </>
  );
};

export default FeedbackWidget;